import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {DoctorDataModel} from '../../models/doctors/doctor-data.model';
import {PrintTreatmentPlanningModel, TreatmentPlanningDataModel} from './models/treatment-planning-data.model';
import {TreatmentPlanningProcedurePseudoModel} from './models/treatment-planning-procedure-pseudo.model';
import {DoctorsService} from '../../services/doctors.service';
import {NewTreatmentPlanRequestModel} from './models/requests/new-treatment-plan-request.model';
import {FeeStructureDataModel} from '../../models/patient/fee-structure-data.model';
import {TreatmentPlanningFormDataModel} from './treatment-planning-form-data.model';
import {tap} from 'rxjs/operators';
import {StaticDataService} from '../../services/static-data.service';
import {CreatePatientCaseModel} from '../../models/caseProcedures/create-patient-case.model';
import {DoctorQueryOptionsModel} from '../../models/doctors/doctor-query-options.model';
import {Globals} from '../../globals';
import { dataPatientCaseWorkItemEventModel } from 'src/app/models/Events/dataPatientCaseWorkItemEvent.model';
import { DataPatientCaseWorkItemModel } from 'src/app/models/accountsReceivable/data-patient-case-work-item.model';

@Injectable({
  providedIn: 'root'
})
export class TreatmentPlanningService {

  constructor(
    private http: HttpClient, private doctorsService: DoctorsService, public staticDataService: StaticDataService,
    private globals: Globals
  ) { }

  getTreatmentPlanList(PatientID: string): Observable<TreatmentPlanningDataModel[]> {
    let url: string;
    const method: string = 'treatment-planning/get-treatment-plan-list';

    url = environment.TDOurl + method + '/' + PatientID;
    return this.http.get<TreatmentPlanningDataModel[]>(url);
  }

  // Treatment Plan
  /////////////////////////////////////////////////////////////////////////////////////////////////
  getTreatmentPlan(treatmentPlanningId: string ): Observable<TreatmentPlanningDataModel[]> {
    const url = environment.TDOurl + 'treatment-planning/get-treatment-plan/' + treatmentPlanningId;
    return this.http.get<TreatmentPlanningDataModel[]>(url);
  }

  getTreatmentPlanByCaseWorkItemFormsId(caseWorkItemFormsId: string ): Observable<TreatmentPlanningDataModel> { 
    const url = `${environment.TDOurl}treatment-planning/get-treatment-plan-By-CaseWorkItemFormsId/${caseWorkItemFormsId}`;
    return this.http.get<TreatmentPlanningDataModel>(url);
  }   

  getTreatmentOptionPlan(PatientID: string ): Observable<TreatmentPlanningDataModel[]> {
    const url = environment.TDOurl + 'treatment-planning/get-treatment-plan-Options-list/' + PatientID;
    return this.http.get<TreatmentPlanningDataModel[]>(url);
  }

  createTreatmentPlan(patientId: string, parentTreatmentId: string = null, isCreateBlankProcedure: boolean = true): Observable<TreatmentPlanningDataModel[]> {
    let url: string;
    const method: string = 'treatment-planning/create-new-treatment-plan';
    url = environment.TDOurl + method;
    const data = new NewTreatmentPlanRequestModel(patientId, parentTreatmentId, isCreateBlankProcedure);
    return this.http.post<TreatmentPlanningDataModel[]>(url, data);
  }

  // Currently only used to save doctor selection
  saveTreatmentPlan(treatmentPlan: any): Observable<any> {
    let url: string;
    const method: string = 'treatment-planning/save-treatment-plan';
    url = environment.TDOurl + method;
    return this.http.post<any>(url, treatmentPlan);
  }

  saveTreatmentPlanDescription(treatmentPlan: any): Observable<any> {

    let url: string;
    const method: string = 'treatment-planning/save-treatment-plan-description';
    url = environment.TDOurl + method;
    return this.http.post<any>(url, treatmentPlan);


  }




  deleteTreatmentPlan(treatmentPlanningId: string) {
    const url = environment.TDOurl + 'treatment-planning/delete-treatment-plan/' + treatmentPlanningId;
    return this.http.get<any>(url);
  }

  /**
   * This can safely be removed after its last usage in PatientPortal is removed.  This is now being handled on the backend as part of the
   * getTreatmentPlan call above.
   * @param treatmentPlanningId The primary key of the current treatment plan.
   */
  getTreatmentPlanProcedure(treatmentPlanningId: string): Observable<TreatmentPlanningProcedurePseudoModel[]> {
    let url: string;
    const method: string = 'treatment-planning/get-treatment-plan-procedure';
    url = environment.TDOurl + method + '/' + treatmentPlanningId;
    return this.http.get<TreatmentPlanningProcedurePseudoModel[]>(url);
  }

  copyProceduresToTreatmentPlan(treatmentPlanningId: string, proceduresToCopy: TreatmentPlanningProcedurePseudoModel[]) {
    let url: string;
    const method = `treatment-planning/copy-treatment-plan-procedures/${treatmentPlanningId}`;
    url = environment.TDOurl + method;
    return this.http.post<TreatmentPlanningProcedurePseudoModel[]>(url, proceduresToCopy);
  }

  moveProceduresToTreatmentPlan(treatmentPlanningId: string, proceduresToMove: TreatmentPlanningProcedurePseudoModel[]) {
    let url: string;
    const method = `treatment-planning/move-treatment-plan-procedures/${treatmentPlanningId}`;
    url = environment.TDOurl + method;
    return this.http.post<TreatmentPlanningProcedurePseudoModel[]>(url, proceduresToMove);
  }

  createTreatmentPlanProcedure(treatmentPlanningId: string) {
    let url: string;
    const method = `treatment-planning/create-blank-treatment-plan-procedure/${treatmentPlanningId}`;
    url = environment.TDOurl + method;
    return this.http.get<TreatmentPlanningProcedurePseudoModel>(url);
  }

  createTreatmentPlanProcedures(treatmentPlanningId: string, teethIds: string[]) {
    let url: string;
    const method = `treatment-planning/create-blank-tooth-treatment-plan-procedures/${treatmentPlanningId}`;
    url = environment.TDOurl + method;
    return this.http.post<TreatmentPlanningProcedurePseudoModel[]>(url, teethIds);
  }

  deleteTreatmentPlanProcedures(procedures: TreatmentPlanningProcedurePseudoModel[]) {
    let url: string;
    const method = 'treatment-planning/delete-treatment-plan-procedures';
    url = environment.TDOurl + method;
    return this.http.post<any>(url, procedures);
  }
/*
  sendTreatmentPlanEmail(patientId: string): Observable<any> {
    let url: string;
    const method: string = `Patient/sendAlertToPatientForTreatment/${patientId}`;

    const host = location.origin;
    const payload = {
      host,
    };

    url = environment.TDOurl + method;
    return this.http.post<any>(url, payload);
  }
    */

  /*saveProceduresForNewOption(TreatmentPlanningID: string, ParentTreatmentPlanningID: string, procList: any): Observable<any> {
    let url: string;
    const method: string = 'TreatmentPlanning/SaveNewOption4TreatmentPlan';
    url = environment.TDOurl + method + '/' + TreatmentPlanningID + '/' + ParentTreatmentPlanningID;
    return this.http.post<any>(url, procList);
  }*/

  saveTreatmentPlanProcedures(procList: TreatmentPlanningProcedurePseudoModel[]): Observable<any> {
    let url: string;
    const method: string = 'treatment-planning/save-treatment-plan-procedures';
    url = environment.TDOurl + method;
    return this.http.post<any>(url, procList);
  }

  // Possibly move this to a service relative to case procedures since we are borrowing from that.  Treatment planning comes before creating
  // a case, but borrowing it's codes to create such makes sense.
  getCaseProceduresByText(DoctorID: string, PatientID: string, ToothId: string, searchText: string): Observable<FeeStructureDataModel[]> {
    let url: string;
    const method: string = 'treatment-planning/procedures';

    url = environment.TDOurl + method + '/' + DoctorID + '/' + PatientID + '/' + ToothId + '/' + searchText;
    return this.http.get<FeeStructureDataModel[]>(url);
  }

  getSelectedDoctorByCaseWorkItemForm(caseWorkItemFormsId: string){
    return this.http.get<dataPatientCaseWorkItemEventModel>(`api/treatment-planning/getSelectedDoctorByCaseWorkItemForm/`+ caseWorkItemFormsId);
  }

  getSelectedDoctorByCaseWorkItemFormID(caseWorkItemFormsId: string){
    return this.http.get<any>(`api/treatment-planning/getSelectedDoctorByCaseWorkItemFormID/`+ caseWorkItemFormsId);
  }

  getDoctors(): Observable<DoctorDataModel[]> {
    const queryModel = new DoctorQueryOptionsModel();
    queryModel.isLazyLoad = false;
    return this.doctorsService.getDoctors(queryModel);
  }

  getFormData(treatmentPlanningProcedureId: string) {
    return this.http.get<TreatmentPlanningFormDataModel>(`api/treatment-planning/get-data-form/${treatmentPlanningProcedureId}`)
      .pipe(
        tap(_ => {
          for (const dataFormParagraph of _.dataFormParagraphs) {
            this.staticDataService.addStaticFormData(dataFormParagraph);
          }
        })
      );
  }

  approveTreatmentPlan(creationModel: CreatePatientCaseModel): Observable<TreatmentPlanningProcedurePseudoModel[]> {
    return this.http.post<TreatmentPlanningProcedurePseudoModel[]>(`api/treatment-planning/approve-treatment-plan`, creationModel);
  }

  getFeeData(option: FeeStructureDataModel) {
    return this.http.post<FeeStructureDataModel>(`api/treatment-planning/get-fee-data-for-item`, option);
  }

  sendPatientTreatmentPlanApproval(treatmentPlanningId: string, sendEmail:boolean, sendSMS:boolean): Observable<TreatmentPlanningDataModel> {
    console.log ("Send Treatment Plan to Patient:", sendEmail, sendSMS );
    return this.http.get<TreatmentPlanningDataModel>(`api/treatment-planning/${treatmentPlanningId}/requestapproval/?e=${sendEmail}&s=${sendSMS}`);
  }
  removePatientTreatmentPlanApproval(treatmentPlanningId: string): Observable<TreatmentPlanningDataModel> {
    return this.http.get<TreatmentPlanningDataModel>(`api/treatment-planning/${treatmentPlanningId}/removeapproval`);
  }
}
